import React, { useState, useEffect, useCallback } from 'react'
import { loadStripe } from '@stripe/stripe-js'
import {
  Elements,
  CardElement,
  useStripe,
  useElements
} from '@stripe/react-stripe-js'
import { Button } from '../ui/Button'
import { Input } from '../ui/Input'
import { Label } from '../ui/Label'
import { useAuthStore } from '../../stores/authStore'
import { paymentService } from '../../services/paymentService-v2'
import { useErrorHandler, BaseError } from '../../utils/errors'
import { 
  CreditCard, 
  Shield, 
  AlertCircle, 
  CheckCircle, 
  Loader,
  Info 
} from 'lucide-react'

// Initialize Stripe
const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY!)

interface PaymentFormProps {
  amount: number
  serviceType: string // Changed from planType
  serviceTitle: string
  onSuccess: (paymentIntentId: string, billingDetails?: any, paymentMethod?: any, actualAmount?: number) => void
  onError: (error: string) => void
  onCancel: () => void
}

const PaymentFormContent: React.FC<PaymentFormProps> = ({
  amount,
  serviceType,
  serviceTitle,
  onSuccess,
  onError,
  onCancel
}) => {
  const stripe = useStripe()
  const elements = useElements()
  const { user } = useAuthStore()
  const { handleError, handleErrorWithCallback } = useErrorHandler()

  // State management - simplified Stripe pattern
  const [isProcessing, setIsProcessing] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false) // Form loads immediately, no initial payment intent needed
  const [cardError, setCardError] = useState<string | null>(null)

  // Billing information state
  const [billingInfo, setBillingInfo] = useState({
    cardholderName: user?.name || '',
    email: user?.email || '',
    companyName: ''
  })
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})

  // Promo code state
  const [promoCode, setPromoCode] = useState('')
  const [promoApplied, setPromoApplied] = useState<{
    valid: boolean
    code: string
    type: 'amount' | 'percent'
    value: number
    savings: number
    description: string
  } | null>(null)
  const [promoError, setPromoError] = useState<string | null>(null)
  const [isValidatingPromo, setIsValidatingPromo] = useState(false)

  // Validation functions
  const validateBillingInfo = () => {
    const errors: Record<string, string> = {}
    
    if (!billingInfo.cardholderName.trim()) {
      errors.cardholderName = 'Cardholder name is required'
    }
    
    if (!billingInfo.email.trim()) {
      errors.email = 'Email address is required'
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(billingInfo.email)) {
      errors.email = 'Please enter a valid email address'
    }
    
    setFieldErrors(errors)
    return Object.keys(errors).length === 0
  }

  const handleBillingInfoChange = (field: string, value: string) => {
    setBillingInfo(prev => ({ ...prev, [field]: value }))
    // Clear field error when user starts typing
    if (fieldErrors[field]) {
      setFieldErrors(prev => ({ ...prev, [field]: '' }))
    }
  }

  // Promo code validation
  const handleApplyPromo = async () => {
    if (!promoCode.trim()) {
      setPromoError('Please enter a promo code')
      return
    }

    setIsValidatingPromo(true)
    setPromoError(null)

    try {
      const result = await paymentService.validatePromoCode(promoCode.trim(), amount)

      if (result.valid) {
        setPromoApplied({
          valid: true,
          code: result.code,
          type: result.type,
          value: result.value,
          savings: result.savings,
          description: result.description
        })
        setPromoError(null)
        console.log('✅ Promo code applied:', result)
      } else {
        setPromoError(result.message || 'Invalid promo code')
        setPromoApplied(null)
      }
    } catch (error) {
      console.error('Promo validation error:', error)
      setPromoError('Failed to validate promo code')
      setPromoApplied(null)
    } finally {
      setIsValidatingPromo(false)
    }
  }

  const handleRemovePromo = () => {
    setPromoCode('')
    setPromoApplied(null)
    setPromoError(null)
  }

  // Calculate final amount with promo discount
  const calculateFinalAmount = () => {
    if (!promoApplied) return amount

    const discountedAmount = amount - promoApplied.savings
    return Math.max(0, Math.round(discountedAmount * 100) / 100)
  }

  const finalAmount = calculateFinalAmount()

  // Create payment intent when user submits (not on mount)
  const createPaymentIntent = async () => {
    try {
      if (!user?.id) {
        throw new Error('User authentication required')
      }

      // IMPORTANT: Send original amount, not discounted amount!
      // Backend will validate promo code and calculate the final discounted amount
      const amountToCharge = amount // Always use original amount

      console.log(`🚀 Creating payment intent for ${serviceType}:`, {
        amount: amountToCharge,
        serviceType,
        userId: user.id,
        promoCode: promoApplied?.code,
        note: promoApplied ? `Promo ${promoApplied.code} will be calculated by backend` : 'No promo'
      })

      const intent = await paymentService.createPaymentIntent({
        amount: amountToCharge,
        serviceType,
        userId: user.id.toString(),
        promoCode: promoApplied?.code || undefined
      })

      if (!intent.success || !intent.clientSecret) {
        throw new Error('Invalid payment intent response')
      }

      return intent
    } catch (error) {
      console.error('❌ Failed to create payment intent:', error)
      throw error
    }
  }

  // No useEffect needed - form loads immediately without payment intent

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault()

    // Validate billing information first
    if (!validateBillingInfo()) {
      return
    }

    if (!stripe || !elements) {
      setError('Payment system is not ready. Please try again.')
      return
    }

    const cardElement = elements.getElement(CardElement)
    if (!cardElement) {
      setError('Payment form is not loaded. Please refresh and try again.')
      return
    }

    try {
      setIsProcessing(true)
      setError(null)
      setCardError(null)

      // Create payment intent when user clicks Pay (not on component mount)
      console.log('💳 Creating payment intent and processing payment...')
      const paymentIntent = await createPaymentIntent()
      
      console.log('✅ Payment intent created:', {
        paymentIntentId: paymentIntent.paymentIntentId,
        amount: paymentIntent.amount
      })

      // Create payment method from card element with billing details
      const { error: paymentMethodError, paymentMethod } = await stripe.createPaymentMethod({
        type: 'card',
        card: cardElement,
        billing_details: {
          name: billingInfo.cardholderName,
          email: billingInfo.email,
          address: billingInfo.companyName ? { line1: billingInfo.companyName } : undefined,
        },
      })

      if (paymentMethodError) {
        console.error('💥 Payment method creation error:', paymentMethodError)
        const errorMessage = getStripeErrorMessage(paymentMethodError)
        setCardError(errorMessage)
        onError(errorMessage)
        return
      }

      // Confirm payment through our service (which will handle emails)
      console.log('⚡ Confirming payment through service binding...')
      const startTime = Date.now()
      const confirmResult = await paymentService.confirmPayment(
        paymentIntent.paymentIntentId,
        paymentMethod!.id
      )
      
      const duration = Date.now() - startTime
      console.log(`✅ Payment confirmed via service binding in ${duration}ms:`, {
        success: confirmResult.success,
        emailsSent: confirmResult.emails.sent
      })

      if (confirmResult.success && confirmResult.status === 'succeeded') {
        console.log('🎉 Payment succeeded with emails:', {
          paymentId: confirmResult.paymentIntentId,
          emailsSent: confirmResult.emails.sent,
          serviceType: confirmResult.serviceType
        })
        
        // Pass billing details, payment method info, and actual amount charged to parent
        // Note: We pass the calculated finalAmount as an estimate
        // The actual charged amount will come from Stripe's payment intent
        const paymentMethodInfo = {
          last4: paymentMethod?.card?.last4,
          brand: paymentMethod?.card?.brand
        }

        // Use the amount from confirmResult if available, otherwise use our calculation
        const actualAmountCharged = confirmResult.amount || finalAmount

        onSuccess(confirmResult.paymentIntentId, billingInfo, paymentMethodInfo, actualAmountCharged)
      } else {
        console.warn('⚠️ Payment not completed:', confirmResult.status)
        setError('Payment was not completed. Please try again.')
      }

    } catch (error) {
      console.error('❌ Payment processing error:', error)
      
      handleErrorWithCallback(error, (err) => {
        setError(err.userMessage)
        onError(err.userMessage)
      })
    } finally {
      setIsProcessing(false)
    }
  }

  const handleCardChange = (event: any) => {
    if (event.error) {
      setCardError(event.error.message)
    } else {
      setCardError(null)
    }
  }

  const getStripeErrorMessage = (error: any): string => {
    const errorMessages: Record<string, string> = {
      'card_declined': 'Your card was declined. Please try a different payment method.',
      'insufficient_funds': 'Your card has insufficient funds. Please try a different payment method.',
      'incorrect_cvc': 'Your card\'s security code is incorrect. Please check and try again.',
      'expired_card': 'Your card has expired. Please try a different payment method.',
      'processing_error': 'An error occurred while processing your card. Please try again.',
      'incorrect_number': 'Your card number is incorrect. Please check and try again.',
    }

    return errorMessages[error.code] || error.message || 'Payment failed. Please try again.'
  }

  // Loading state (only show if explicitly loading something)
  if (isLoading) {
    return (
      <div className="text-center p-8">
        <Loader className="w-8 h-8 animate-spin mx-auto mb-4 text-blue-600" />
        <h3 className="text-lg font-semibold mb-2">Loading Payment Form</h3>
        <p className="text-gray-600 text-sm">
          Preparing secure payment interface...
        </p>
      </div>
    )
  }

  return (
    <div className="space-y-6">
        {/* Service Summary */}
        <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
          <div className="flex justify-between items-center mb-2">
            <span className="font-medium text-gray-900">Service</span>
            <span className="text-lg font-semibold text-gray-900">${amount.toFixed(2)} USD</span>
          </div>
          <div className="text-sm text-gray-600 mb-3">
            {serviceTitle}
          </div>

          {/* Promo Code Section */}
          {!promoApplied ? (
            <div className="pt-3 border-t border-blue-200">
              <div className="flex gap-2">
                <Input
                  type="text"
                  placeholder="Promo code"
                  value={promoCode}
                  onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                  onKeyPress={(e) => e.key === 'Enter' && handleApplyPromo()}
                  disabled={isValidatingPromo}
                  className="flex-1 text-sm"
                />
                <Button
                  type="button"
                  onClick={handleApplyPromo}
                  disabled={!promoCode.trim() || isValidatingPromo}
                  variant="outline"
                  size="sm"
                  className="whitespace-nowrap"
                >
                  {isValidatingPromo ? <Loader className="w-4 h-4 animate-spin" /> : 'Apply'}
                </Button>
              </div>
              {promoError && (
                <div className="mt-2 text-xs text-red-600 flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" />
                  {promoError}
                </div>
              )}
            </div>
          ) : (
            <div className="pt-3 border-t border-blue-200">
              <div className="flex justify-between items-center mb-2">
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-600" />
                  <span className="text-sm text-gray-700">Discount ({promoApplied.code})</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold text-green-600">-${promoApplied.savings.toFixed(2)}</span>
                  <button
                    type="button"
                    onClick={handleRemovePromo}
                    className="text-gray-400 hover:text-gray-600 text-xs"
                  >
                    Remove
                  </button>
                </div>
              </div>
              <div className="text-xs text-green-700 bg-green-50 rounded px-2 py-1">
                ✓ {promoApplied.description} applied
              </div>
            </div>
          )}

          {/* Total */}
          <div className="pt-3 border-t border-blue-200 mt-3">
            <div className="flex justify-between items-center">
              <span className="font-semibold text-gray-900">Total</span>
              <span className="text-2xl font-bold text-blue-600">${finalAmount.toFixed(2)} USD</span>
            </div>
            {promoApplied && promoApplied.savings > 0 && (
              <div className="text-xs text-gray-500 text-right mt-1">
                You save ${promoApplied.savings.toFixed(2)}!
              </div>
            )}
          </div>
          <div className="text-xs text-gray-500 mt-1">
            One-time payment for your selected service
          </div>
        </div>

      {/* Payment Form */}
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Billing Information */}
        <div className="space-y-4">
          <h4 className="text-sm font-medium text-gray-900 mb-3">Billing Information</h4>
          
          {/* Responsive grid for billing fields */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="cardholderName" className="text-sm font-medium text-gray-700">
                Cardholder Name *
              </Label>
              <Input
                id="cardholderName"
                type="text"
                value={billingInfo.cardholderName}
                onChange={(e) => handleBillingInfoChange('cardholderName', e.target.value)}
                className={fieldErrors.cardholderName ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : ''}
                placeholder="John Doe"
              />
              {fieldErrors.cardholderName && (
                <div className="mt-1 text-sm text-red-600 flex items-center gap-1">
                  <AlertCircle className="w-4 h-4" />
                  {fieldErrors.cardholderName}
                </div>
              )}
            </div>
            
            <div>
              <Label htmlFor="email" className="text-sm font-medium text-gray-700">
                Email Address *
              </Label>
              <Input
                id="email"
                type="email"
                value={billingInfo.email}
                onChange={(e) => handleBillingInfoChange('email', e.target.value)}
                className={fieldErrors.email ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : ''}
                placeholder="john@example.com"
              />
              {fieldErrors.email && (
                <div className="mt-1 text-sm text-red-600 flex items-center gap-1">
                  <AlertCircle className="w-4 h-4" />
                  {fieldErrors.email}
                </div>
              )}
            </div>
          </div>
          
          {/* Company name - full width */}
          <div>
            <Label htmlFor="companyName" className="text-sm font-medium text-gray-700">
              Company Name (Optional)
            </Label>
            <Input
              id="companyName"
              type="text"
              value={billingInfo.companyName}
              onChange={(e) => handleBillingInfoChange('companyName', e.target.value)}
              placeholder="Your Company Name"
            />
          </div>
        </div>

        {/* Card Information */}
        <div>
          <Label className="text-sm font-medium text-gray-700 mb-2 block">
            Card Information *
          </Label>
          <div className="border border-gray-300 rounded-md p-3 bg-white focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-blue-500">
            <CardElement
              onChange={handleCardChange}
              options={{
                style: {
                  base: {
                    fontSize: '16px',
                    color: '#374151',
                    '::placeholder': {
                      color: '#9CA3AF',
                    },
                  },
                  invalid: {
                    color: '#EF4444',
                  },
                },
              }}
            />
          </div>
          {cardError && (
            <div className="mt-2 text-sm text-red-600 flex items-center gap-1">
              <AlertCircle className="w-4 h-4" />
              {cardError}
            </div>
          )}
        </div>

        {/* General Error Display */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3">
            <div className="flex items-start gap-2">
              <AlertCircle className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
              <div className="text-sm text-red-700">{error}</div>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex gap-3 pt-2">
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            disabled={isProcessing}
            className="flex-1"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={!stripe || isProcessing || !!cardError || Object.keys(fieldErrors).some(key => fieldErrors[key])}
            className="flex-1 bg-blue-600 hover:bg-blue-700"
          >
            {isProcessing ? (
              <div className="flex items-center gap-2">
                <Loader className="w-4 h-4 animate-spin" />
                <span>Processing... ⚡</span>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4" />
                Pay ${finalAmount.toFixed(2)}
              </div>
            )}
          </Button>
        </div>
      </form>

      {/* Additional Info */}
      <div className="border-t pt-4">
        <div className="flex items-start gap-2 text-xs text-gray-500">
          <Info className="w-3 h-3 mt-0.5 flex-shrink-0" />
          <div>
            By completing this payment, you agree to our Terms of Service and Privacy Policy. 
            Your service will be activated within 24 hours of successful payment.
          </div>
        </div>
      </div>
    </div>
  )
}

export const StripePaymentForm: React.FC<PaymentFormProps> = (props) => {
  return (
    <Elements stripe={stripePromise}>
      <PaymentFormContent {...props} />
    </Elements>
  )
}

export default StripePaymentForm